import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type FormEvent,
} from "react";
import { useSearchParams } from "react-router-dom";
import useSWR, { useSWRConfig } from "swr";

import { useModalDismiss } from "../hooks/useModalDismiss";
import { api, type SupportTicket, type SupportTicketDetail } from "../services/api";

import { FeedbackModal } from "./FeedbackModal";
import { Icon } from "./Icon";

/**
 * Settings → Help & Support.
 *
 * The support-ticket history that used to be the standalone "My Feedback" page.
 * New tickets are raised through the shared FeedbackModal (the same form the
 * top-right ⓘ button opens); replies are handled in the detail modal. Open
 * tickets show first, with closed ones tucked behind a collapsible group.
 */

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Australia/Perth",
  });
}

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString("en-AU", {
    dateStyle: "medium",
    timeZone: "Australia/Perth",
  });
}

const CATEGORY_LABEL: Record<SupportTicket["category"], string> = {
  bug: "Bug",
  feature: "Idea",
  question: "Question",
  other: "Other",
};

// Category accent mirrors the Send-feedback form's intent: bugs read as
// critical, ideas as the brand violet, questions as "waiting on us".
const CATEGORY_BADGE: Record<SupportTicket["category"], string> = {
  bug: "badge-critical",
  feature: "badge-open",
  question: "badge-waiting",
  other: "badge-closed",
};

// Reply-image attachments (issue #63): a user can add screenshots to a report
// after submitting. Mirrors the Send-feedback form's limits.
const ALLOWED_MIME = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_ATTACHMENTS = 5;

interface PendingImage {
  localId: string;
  file: File;
  previewUrl: string;
  uploadedUrl: string | null;
  uploading: boolean;
  error?: string;
}

function TicketRow({
  ticket,
  onClick,
}: {
  ticket: SupportTicket;
  onClick: () => void;
}) {
  return (
    <button type="button" className="hs-ticket" onClick={onClick}>
      <div className="hs-ticket-body">
        <div className="hs-ticket-subject">
          {ticket.subject}
          {ticket.has_unread_support_reply && (
            <span className="badge badge-open" style={{ marginLeft: 8 }}>
              New reply
            </span>
          )}
        </div>
        <div className="hs-ticket-tags">
          <span className={`badge ${CATEGORY_BADGE[ticket.category]}`}>
            {CATEGORY_LABEL[ticket.category]}
          </span>
          <span
            className={`badge ${
              ticket.status === "open" ? "badge-progress" : "badge-closed"
            }`}
          >
            {ticket.status}
          </span>
          <span className="hs-ticket-date">{fmtDay(ticket.updated_at)}</span>
        </div>
      </div>
      <Icon name="chevRight" size={18} className="hs-ticket-chev" />
    </button>
  );
}

export function HelpSupport() {
  const { data: tickets, mutate, isValidating } = useSWR<SupportTicket[]>(
    "/api/feedback",
    () => api.feedback.list(),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // A support-reply notification in the bell links here with ?support=<id>,
  // which opens that conversation (#191).
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedId = searchParams.get("support");
  useEffect(() => {
    if (linkedId) setSelectedId(linkedId);
  }, [linkedId]);
  const [showNew, setShowNew] = useState(false);
  const [showClosed, setShowClosed] = useState(false);

  const open = (tickets ?? []).filter((t) => t.status === "open");
  const closed = (tickets ?? []).filter((t) => t.status === "closed");

  return (
    <div className="card" style={{ padding: 24 }}>
      <div className="hs-head">
        <div>
          <h2 className="card-title">Your support tickets</h2>
          <p className="card-desc" style={{ marginTop: 4 }}>
            Report a bug, suggest an idea, or ask the iQuest team a question, then
            track replies here.
          </p>
        </div>
        <div className="hs-head-actions">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setShowNew(true)}
          >
            <Icon name="plus" size={16} />
            <span>New</span>
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => void mutate()}
            disabled={isValidating}
            aria-label="Refresh tickets"
            title="Refresh"
          >
            <Icon name="refresh" size={16} />
          </button>
        </div>
      </div>

      {!tickets ? (
        <div className="hs-empty">Loading…</div>
      ) : tickets.length === 0 ? (
        <div className="hs-empty">
          <p>No support tickets yet.</p>
          <p className="empty-state-blurb">
            Select <strong>New</strong> to send a bug, idea, or question to the
            iQuest team.
          </p>
        </div>
      ) : (
        <div className="hs-list">
          {open.length === 0 && <div className="hs-empty hs-empty-sm">No open tickets.</div>}
          {open.map((t) => (
            <TicketRow key={t.id} ticket={t} onClick={() => setSelectedId(t.id)} />
          ))}

          {closed.length > 0 && (
            <>
              <button
                type="button"
                className="hs-closed-toggle"
                aria-expanded={showClosed}
                onClick={() => setShowClosed((v) => !v)}
              >
                <Icon
                  name="chevRight"
                  size={15}
                  className={`hs-closed-chev${showClosed ? " open" : ""}`}
                />
                <span>Older closed tickets ({closed.length})</span>
              </button>
              {showClosed &&
                closed.map((t) => (
                  <TicketRow key={t.id} ticket={t} onClick={() => setSelectedId(t.id)} />
                ))}
            </>
          )}
        </div>
      )}

      {selectedId && (
        <TicketDetail
          ticketId={selectedId}
          onClose={() => {
            setSelectedId(null);
            if (linkedId) {
              // Drop the link so the same bell entry can open it again.
              const next = new URLSearchParams(searchParams);
              next.delete("support");
              setSearchParams(next, { replace: true });
            }
            void mutate();
          }}
        />
      )}

      <FeedbackModal
        isOpen={showNew}
        onClose={() => setShowNew(false)}
        onSuccess={() => void mutate()}
      />
    </div>
  );
}

function TicketDetail({
  ticketId,
  onClose,
}: {
  ticketId: string;
  onClose: () => void;
}) {
  const { data: ticket, mutate } = useSWR<SupportTicketDetail>(
    `/api/feedback/${ticketId}`,
    () => api.feedback.get(ticketId),
  );
  const [reply, setReply] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<PendingImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const panelRef = useModalDismiss(onClose);
  const { mutate: revalidate } = useSWRConfig();

  // Opening the conversation reads it. The server also clears its bell rows,
  // so refresh the user-pill dot and the bell now rather than on their poll.
  useEffect(() => {
    void api.feedback
      .markRead(ticketId)
      .then(() =>
        revalidate(
          (key) =>
            typeof key === "string" &&
            (key === "/api/feedback/unread-count" || key.startsWith("/api/notifications")),
        ),
      )
      .catch(() => {});
  }, [ticketId, revalidate]);

  const processFiles = useCallback(
    async (files: File[]) => {
      setError(null);
      const remaining = MAX_ATTACHMENTS - attachments.length;
      if (remaining <= 0) {
        setError(`Maximum ${MAX_ATTACHMENTS} images`);
        return;
      }
      const accepted: PendingImage[] = [];
      for (const f of files.slice(0, remaining)) {
        if (!ALLOWED_MIME.includes(f.type)) {
          setError(`"${f.name}" is not a supported image type (PNG, JPEG, GIF, WebP).`);
          continue;
        }
        if (f.size > MAX_FILE_SIZE) {
          setError(`"${f.name}" exceeds the 5 MB limit.`);
          continue;
        }
        accepted.push({
          localId: `${f.name}-${f.size}-${f.lastModified}`,
          file: f,
          previewUrl: URL.createObjectURL(f),
          uploadedUrl: null,
          uploading: true,
        });
      }
      if (!accepted.length) return;
      setAttachments((prev) => [...prev, ...accepted]);
      for (const a of accepted) {
        try {
          const res = await api.feedback.uploadAttachment(a.file);
          setAttachments((prev) =>
            prev.map((p) =>
              p.localId === a.localId ? { ...p, uploadedUrl: res.url, uploading: false } : p,
            ),
          );
        } catch (err) {
          const msg = err instanceof Error ? err.message : "upload failed";
          setAttachments((prev) =>
            prev.map((p) =>
              p.localId === a.localId ? { ...p, uploading: false, error: msg } : p,
            ),
          );
        }
      }
    },
    [attachments.length],
  );

  const handleFilePick = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) void processFiles(Array.from(e.target.files));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handlePaste = (e: ClipboardEvent) => {
    const imgs = Array.from(e.clipboardData?.items ?? [])
      .filter((it) => it.kind === "file" && ALLOWED_MIME.includes(it.type))
      .map((it) => it.getAsFile())
      .filter((f): f is File => f !== null);
    if (imgs.length) void processFiles(imgs);
  };

  const removeAttachment = (localId: string) => {
    setAttachments((prev) => {
      const removed = prev.find((p) => p.localId === localId);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((p) => p.localId !== localId);
    });
  };

  const uploadsInFlight = attachments.some((a) => a.uploading);
  const uploadedUrls = attachments
    .filter((a) => a.uploadedUrl && !a.error)
    .map((a) => a.uploadedUrl as string);
  // A reply needs text OR at least one uploaded image; an in-flight upload
  // blocks so we never send before an attachment's URL is known.
  const canSend = (reply.trim().length > 0 || uploadedUrls.length > 0) && !uploadsInFlight;

  async function handleReply(e: FormEvent) {
    e.preventDefault();
    if (!canSend || posting) return;
    setPosting(true);
    setError(null);
    try {
      await api.feedback.reply(
        ticketId,
        reply.trim(),
        uploadedUrls.length ? uploadedUrls : undefined,
      );
      setReply("");
      attachments.forEach((a) => URL.revokeObjectURL(a.previewUrl));
      setAttachments([]);
      await mutate();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send reply");
    } finally {
      setPosting(false);
    }
  }

  if (!ticket) return null;

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-panel modal-panel-lg" ref={panelRef}>
        <header className="modal-header">
          <div>
            <h2 className="modal-title">{ticket.subject}</h2>
            <p className="modal-subtitle">
              {CATEGORY_LABEL[ticket.category]} ·{" "}
              <span
                className={
                  ticket.status === "open"
                    ? "badge badge-progress"
                    : "badge badge-closed"
                }
              >
                {ticket.status}
              </span>{" "}
              · Opened {fmtDateTime(ticket.created_at)}
            </p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="modal-body">
          <div className="ticket-thread">
            <div className="ticket-thread-message ticket-thread-message-user">
              <div className="ticket-thread-meta">You · {fmtDateTime(ticket.created_at)}</div>
              <div className="ticket-thread-body">{ticket.description}</div>
            </div>
            {ticket.comments.map((c) => (
              <div
                key={c.id}
                className={`ticket-thread-message ${
                  c.source === "support"
                    ? "ticket-thread-message-support"
                    : "ticket-thread-message-user"
                }`}
              >
                <div className="ticket-thread-meta">
                  {c.source === "support" ? `${c.author_name ?? "iQuest"} (iQuest)` : "You"} ·{" "}
                  {fmtDateTime(c.created_at)}
                </div>
                <div className="ticket-thread-body">{c.content}</div>
              </div>
            ))}
          </div>

          <form onSubmit={handleReply} className="ticket-reply-form">
            <label htmlFor="ticket-reply">Reply</label>
            <textarea
              id="ticket-reply"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onPaste={handlePaste}
              rows={4}
              placeholder="Add more context, respond to a question, or paste a screenshot…"
              disabled={posting}
            />
            {attachments.length > 0 && (
              <div className="attachment-grid">
                {attachments.map((a) => (
                  <div key={a.localId} className="attachment-thumb">
                    <img src={a.previewUrl} alt={a.file.name} />
                    {a.uploading && <div className="attachment-overlay">Uploading…</div>}
                    {a.error && (
                      <div className="attachment-overlay attachment-overlay-error">Failed</div>
                    )}
                    <button
                      type="button"
                      className="attachment-remove"
                      onClick={() => removeAttachment(a.localId)}
                      aria-label={`Remove ${a.file.name}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {error && <div className="form-error">{error}</div>}
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_MIME.join(",")}
              multiple
              hidden
              onChange={handleFilePick}
            />
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={posting || attachments.length >= MAX_ATTACHMENTS}
                title="Attach a screenshot (PNG, JPEG, GIF, WebP)"
              >
                <Icon name="upload" size={15} /> Add image
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!canSend || posting}
              >
                {posting ? "Sending…" : "Send reply"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
